import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Typography } from "@mui/material";
import { useGlobalSettingsRead } from "hooks";
import { PriceCalc, PriceCalcWithBasis, Product } from "types";
import { CustomDialog, CustomTab, CustomTabs, Loading } from "core";
import {
  CardmarketFoilIcon,
  CardmarketSyncIcon,
  StoreIcon,
} from "modules/icons";
import { getPriceCalc, getPriceCalcWithBasis } from "modules/pricecalcs/api";
import { isProductPriceFailed } from "modules/products/business";
import { getPriceCalcFailedReasonName } from "modules/shared/options/PriceCalcFailedReasonOption";
import {
  PriceCalcTarget,
  PriceCalculationMethod,
} from "../PriceCalculationMethod";

type ProductPriceCalculationMethodDialogDialogProps = {
  dialogOpen: boolean;
  setDialogOpen: (value: React.SetStateAction<boolean>) => void;
  product: Product;
};

/** Reihenfolge der Tabs. */
const TAB_TARGETS: PriceCalcTarget[] = [
  "Store",
  "CardmarketNoFoil",
  "CardmarketFoil",
  "StoreOld",
  "CardmarketNoFoilOld",
  "CardmarketFoilOld",
];

interface LoadedCalcs {
  store: PriceCalcWithBasis | null;
  storeOld: PriceCalcWithBasis | null;
  online: PriceCalcWithBasis | null;
  onlineOld: PriceCalcWithBasis | null;
  /** Letzte Berechnung ohne verwertbaren Preis; nur die Zeile, keine Grundlagen. */
  failed: PriceCalc | null;
}

export const ProductPriceCalculationMethodDialog: React.FC<
  ProductPriceCalculationMethodDialogDialogProps
> = ({ dialogOpen, setDialogOpen, product }) => {
  const { globalSettings } = useGlobalSettingsRead();

  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [loaded, setLoaded] = useState<LoadedCalcs | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const loadCalcMethodDetails = useCallback(async () => {
    setIsLoading(true);

    // Jede Zeile nur einmal laden: Laden- und Online-Zeiger zeigen meist auf
    // dieselbe Berechnung, und jede Zeile zieht ihre Grundlagen aus S3 nach.
    const distinctIDs = [
      ...new Set(
        [
          product.storePriceCalcID,
          product.storePriceOldCalcID,
          product.onlinePriceCalcID,
          product.onlinePriceOldCalcID,
        ].filter((id): id is string => Boolean(id)),
      ),
    ];

    const [withBasis, failed] = await Promise.all([
      Promise.all(distinctIDs.map((id) => getPriceCalcWithBasis(id))),
      product.lastFailedPriceCalcID
        ? getPriceCalc(product.lastFailedPriceCalcID)
        : Promise.resolve(null),
    ]);

    const byID = new Map(
      distinctIDs.map((id, index) => [id, withBasis[index]]),
    );
    const pick = (id: string | null | undefined) =>
      id ? (byID.get(id) ?? null) : null;

    setLoaded({
      store: pick(product.storePriceCalcID),
      storeOld: pick(product.storePriceOldCalcID),
      online: pick(product.onlinePriceCalcID),
      onlineOld: pick(product.onlinePriceOldCalcID),
      failed,
    });

    setIsLoading(false);
  }, [product]);

  useEffect(() => {
    loadCalcMethodDetails();
  }, [loadCalcMethodDetails]);

  const priceCalcTarget: PriceCalcTarget = TAB_TARGETS[selectedTab] ?? "Store";

  const current = useMemo(() => {
    if (!loaded) {
      return null;
    }

    switch (priceCalcTarget) {
      case "CardmarketFoilOld":
      case "CardmarketNoFoilOld":
        return loaded.onlineOld;
      case "StoreOld":
        return loaded.storeOld;
      case "CardmarketFoil":
      case "CardmarketNoFoil":
        return loaded.online;
      default:
        return loaded.store;
    }
  }, [loaded, priceCalcTarget]);

  const priceCalculationResult = useMemo(() => {
    const priceCalc = current?.priceCalc;

    if (!priceCalc) {
      return null;
    }

    switch (priceCalcTarget) {
      case "CardmarketFoil":
      case "CardmarketFoilOld":
        return priceCalc.priceCalculationOnlineFoil;
      case "CardmarketNoFoil":
      case "CardmarketNoFoilOld":
        return priceCalc.priceCalculationOnline;
      default:
        return priceCalc.priceCalculationStore;
    }
  }, [current, priceCalcTarget]);

  const failedCalc = loaded?.failed ?? null;
  const failedArticleCount = failedCalc?.articleCount ?? null;

  return (
    <CustomDialog
      dialogOpen={dialogOpen}
      positive={true}
      titleText="Rechenweg für Preisberechnung"
      setDialogOpen={setDialogOpen}
      showConfirm={false}
      showDecline={true}
      minWidth={700}
    >
      {/* Der Grund kommt aus dem Produkt: bei einer fehlgeschlagenen Karte zeigen
          storePriceCalcID/onlinePriceCalcID noch auf den letzten ERFOLGREICHEN
          Lauf. Die Details (Zeitpunkt, Angebote, Fehlermeldung) liefert die
          Zeile hinter lastFailedPriceCalcID, ohne Zugriff auf S3. */}
      {isProductPriceFailed(product) && (
        <Typography variant="body2" style={{ marginBottom: 16 }}>
          <strong>Letzte Berechnung fehlgeschlagen:</strong>{" "}
          {getPriceCalcFailedReasonName(product.lastPriceCalcFailedReason)}
          {failedCalc && (
            <>
              {" "}
              (am {new Date(failedCalc.startTime).toLocaleString("de-DE")}
              {failedArticleCount !== null
                ? `, ${failedArticleCount} Angebote gefunden`
                : ""}
              )
              {failedCalc.errorMessage && (
                <>
                  <br />
                  {failedCalc.errorMessage}
                </>
              )}
            </>
          )}
        </Typography>
      )}

      <CustomTabs selectedTab={selectedTab} setSelectedTab={setSelectedTab}>
        <CustomTab tabLabel="Laden" tabIcon={<StoreIcon />} />
        <CustomTab
          tabLabel="Cardmarket - Kein Foil"
          tabIcon={<CardmarketSyncIcon />}
        />
        <CustomTab
          tabLabel="Cardmarket - Foil"
          tabIcon={<CardmarketFoilIcon />}
        />
        <CustomTab tabLabel="Laden (alt)" tabIcon={<StoreIcon />} />
        <CustomTab
          tabLabel="Cardmarket - Kein Foil (alt)"
          tabIcon={<CardmarketSyncIcon />}
        />
        <CustomTab
          tabLabel="Cardmarket - Foil (alt)"
          tabIcon={<CardmarketFoilIcon />}
        />
      </CustomTabs>

      {isLoading ? (
        <Loading
          align="center"
          description="Bitte warten. Der Rechenweg wird geladen..."
        />
      ) : !globalSettings ? (
        <Typography variant="h3">
          Die globalen Einstellungen für die Preisberechnung konnten nicht
          geladen werden.
        </Typography>
      ) : !current ? (
        <Typography variant="h3">
          Es wurde noch kein Rechenweg für die Preisberechnung hinterlegt.
        </Typography>
      ) : (
        <PriceCalculationMethod
          priceCalcTarget={priceCalcTarget}
          priceCalc={current.priceCalc}
          articles={current.articles}
          basisMissing={current.basisMissing}
          priceCalculationResult={priceCalculationResult}
          globalSettings={globalSettings}
        />
      )}
    </CustomDialog>
  );
};
